import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const FAQS = [
	{
		category: 'Project & Estimates',
		items: [
			{
				q: 'How do I get a free estimate?',
				a: 'Simply fill out our contact form or call us at (602) 390-2536. We\'ll schedule a free in-home consultation at a time that works for you. After reviewing the project in person, we provide a detailed written estimate within 24–48 hours.',
			},
			{
				q: 'How long does a typical remodel take?',
				a: 'It depends on the scope. A bathroom refresh can take 5–7 days. A full kitchen remodel typically takes 10–18 days. Flooring replacement throughout a home can be done in 2–4 days. We provide a projected timeline in every written estimate.',
			},
			{
				q: 'Do you require a deposit?',
				a: 'Yes, we require a deposit of 30–40% before ordering materials. The balance is split into scheduled draws tied to project milestones. We never ask for more than 10% retention at project close — this protects both parties.',
			},
			{
				q: 'Is your estimate binding? Can the price change?',
				a: 'Our estimates are detailed and itemized. The price we quote is the price you pay — unless the scope changes due to unexpected conditions discovered during demo (such as water damage or code violations), in which case we stop work, explain the issue, and get your written approval before proceeding.',
			},
			{
				q: 'Do you handle permits?',
				a: 'Yes. A-Z Home Improvement pulls all required building permits through the City of Phoenix or relevant municipality. Permitted work protects you as a homeowner and is often required for insurance claims and resale. Permit costs are included in the estimate.',
			},
		],
	},
	{
		category: 'Licensing & Insurance',
		items: [
			{
				q: 'Is A-Z Home Improvement licensed in Arizona?',
				a: 'Yes. We are fully licensed under the Arizona Registrar of Contractors. Our license number is AZ ROC #XXXXX8142. You can verify our license status at the Arizona ROC website at roc.az.gov.',
			},
			{
				q: 'Are you insured?',
				a: 'Yes. We carry $2 million in general liability insurance and maintain active workers\' compensation coverage for all employees and subcontractors on site. A certificate of insurance is available upon request before any project begins.',
			},
			{
				q: 'Do you use subcontractors?',
				a: 'For certain specialty work (electrical, plumbing, HVAC), we use licensed, pre-vetted subcontractors who carry their own insurance. We coordinate and supervise all subcontractor work and you deal with A-Z directly throughout the entire project.',
			},
		],
	},
	{
		category: 'Warranty & Support',
		items: [
			{
				q: 'What warranty do you offer on your work?',
				a: 'We back every installation with a 5-year workmanship warranty. If something fails due to our installation within five years, we return and fix it at no charge. Materials carry their own manufacturer warranties, which we help you register.',
			},
			{
				q: 'What if I\'m not satisfied with the work?',
				a: 'We do a final walkthrough with you before closing out any project. If you identify anything that isn\'t right, we address it before you make the final payment. Your satisfaction is not optional — it\'s how we\'ve built our reputation in Phoenix.',
			},
			{
				q: 'How do I reach you if I have a post-project issue?',
				a: 'Call us at (602) 390-2536 or email support@azhomefundingservices.com. For warranty claims, we respond within one business day and schedule a site visit within 72 hours for most issues.',
			},
		],
	},
	{
		category: 'SMS & Text Message Communications',
		items: [
			{
				q: 'Why am I receiving text messages from A-Z Home Improvement?',
				a: 'You provided your phone number and opted in to receive SMS communications from A-Z Home Improvement LLC. Our messages include appointment confirmations, estimate reminders, project update notifications, and promotional offers.',
			},
			{
				q: 'How do I stop receiving text messages?',
				a: 'Reply STOP to any message from us at any time. You will receive one final confirmation and no further messages will be sent. You can also email us at support@azhomefundingservices.com to opt out.',
			},
			{
				q: 'How do I get help with text messages?',
				a: 'Reply HELP to any message. You can also call us at (602) 390-2536 or email support@azhomefundingservices.com. We respond to all text inquiries within one business day.',
			},
			{
				q: 'Will you sell my phone number to third parties?',
				a: 'No. We do not sell, rent, or share your phone number or any personal information with third parties for marketing purposes. Your information is used solely to communicate with you about A-Z Home Improvement services.',
			},
			{
				q: 'What are the message and data rates for your SMS program?',
				a: 'Standard message and data rates from your mobile carrier may apply. A-Z Home Improvement does not charge for text messages. Message frequency varies — typically 2–6 messages per month depending on your active project status.',
			},
		],
	},
];

function FAQ() {
	const [open, setOpen] = useState({});
	const toggle = key => setOpen(prev => ({ ...prev, [key]: !prev[key] }));

	return (
		<>
			<Helmet>
				<title>FAQ | A-Z Home Improvement LLC | Phoenix, AZ</title>
				<meta name="description" content="Frequently asked questions about A-Z Home Improvement LLC's remodeling services, licensing, warranties, and SMS communications." />
			</Helmet>
			<PageHeader
				title="Frequently Asked Questions"
				subtitle="Everything you need to know about working with A-Z Home Improvement LLC."
			/>

			<section className="section">
				<div className="container" style={{ maxWidth: 780 }}>
					{FAQS.map(cat => (
						<div key={cat.category} style={{ marginBottom: 40 }}>
							<h2 style={{ borderBottom: '2px solid var(--brand)', paddingBottom: 8, marginBottom: 20 }}>{cat.category}</h2>
							{cat.items.map((item, i) => {
								const key = `${cat.category}-${i}`;
								return (
									<div key={key} className="card" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => toggle(key)}>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontWeight: 700 }}>
											<span style={{ fontSize: 15 }}>{item.q}</span>
											<span style={{ fontSize: 20, color: 'var(--brand)', flexShrink: 0 }}>{open[key] ? '−' : '+'}</span>
										</div>
										{open[key] && (
											<p className="helper" style={{ margin: '12px 0 0', fontSize: 14 }}>{item.a}</p>
										)}
									</div>
								);
							})}
						</div>
					))}

					<div className="card" style={{ textAlign: 'center', background: 'var(--brand)', color: '#fff', padding: 28 }}>
						<h3 style={{ margin: '0 0 8px', color: '#fff' }}>Have a Question Not Listed Here?</h3>
						<p style={{ margin: '0 0 16px', opacity: 0.9 }}>Call or email us — we're happy to answer any question before you commit to anything.</p>
						<div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
							<a href="tel:+16023902536" className="btn-outline" style={{ color: '#fff', borderColor: '#fff' }}>(602) 390-2536</a>
							<Link to="/contact" style={{ background: '#fff', color: 'var(--brand)', padding: '10px 20px', borderRadius: 6, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>Send a message</Link>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}

export default FAQ;
